import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import styles from './styles/GiroEstoque.module.css';
import Link from '../components/Link';
import PageTitle from '../components/PageTitle';
import Loading from '../components/Loading/Index';

const API_URL = 'https://provin.ind.br/api';

const initialFiltros = {
    setores: [],
    regioes: [],
    celulas: [],
    periodoIni: '2026-01-01',
    periodoFim: '2026-12-31',
    tabelaPreco: '10',
    produtos: '',
    descricao: '',
    grupoPai: '',
    grupoFilho: '',
    fabricante: '',
    fornecedor: '',
    contabilizarAlmoxarifado: 'NAO'
};

const initialOptions = {
    setores: [],
    regioes: [],
    celulas: [],
    gruposPai: [],
    gruposFilho: [],
    fabricantes: [],
    fornecedores: []
};

function formatNumber(value) {
    return Number(value || 0).toLocaleString('pt-BR');
}

function formatCurrency(value) {
    return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    });
}

function normalizeArray(value) {
    return Array.isArray(value) ? value : [];
}

function CheckboxGroup({ title, options, selectedValues, onChange }) {
    const handleToggle = (value) => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter((item) => item !== value));
            return;
        }

        onChange([...selectedValues, value]);
    };

    return (
        <div className={styles.filterBlock}>
            <div className={styles.checkboxGroupHeader}>
                <div className={styles.filterLabel}>{title}</div>
                {options.length > 0 && (
                    <div className={styles.checkboxGroupActions}>
                        <button
                            type="button"
                            className={styles.checkboxActionBtn}
                            onClick={() => onChange(options)}
                        >
                            Marcar tudo
                        </button>
                        <button
                            type="button"
                            className={styles.checkboxActionBtn}
                            onClick={() => onChange([])}
                        >
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.checkboxGroup}>
                {options.length === 0 ? (
                    <span className={styles.emptyOption}>Nenhuma opção</span>
                ) : (
                    options.map((option) => (
                        <label key={option} className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                checked={selectedValues.includes(option)}
                                onChange={() => handleToggle(option)}
                            />
                            <span>{option}</span>
                        </label>
                    ))
                )}
            </div>
        </div>
    );
}

function SelectField({ title, value, onChange, options, placeholder = 'Todos' }) {
    return (
        <div className={styles.filterBlock}>
            <label className={styles.filterLabel}>{title}</label>

            <select
                className={styles.select}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">{placeholder}</option>

                {options.map((option) => {
                    if (typeof option === 'object' && option !== null) {
                        return (
                            <option key={option.codigo ?? option.nome} value={option.codigo}>
                                {option.nome}
                            </option>
                        );
                    }

                    return (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    );
                })}
            </select>
        </div>
    );
}

function InputField({ title, value, onChange, placeholder = '', type = 'text' }) {
    return (
        <div className={styles.filterBlock}>
            <label className={styles.filterLabel}>{title}</label>

            <input
                className={styles.input}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
}

function GrupoHierarquicoCompacto({ grupos, storageKeyPrefix = 'grupo' }) {
    const lista = normalizeArray(grupos);
    const [expandedMap, setExpandedMap] = useState({});

    useEffect(() => {
        const initialState = {};
        lista.forEach((grupoPai) => {
            initialState[grupoPai.grupoPai] = false;
        });
        setExpandedMap(initialState);
    }, [lista, storageKeyPrefix]);

    const handleToggle = (grupoPai) => {
        setExpandedMap((prev) => ({
            ...prev,
            [grupoPai]: !prev[grupoPai]
        }));
    };

    if (lista.length === 0) {
        return <div className={styles.emptyMini}>Sem detalhamento.</div>;
    }

    return (
        <ul className={styles.groupList}>
            {lista.map((grupoPai) => {
                const isExpanded = !!expandedMap[grupoPai.grupoPai];
                const filhos = normalizeArray(grupoPai.filhos);

                return (
                    <li key={`${storageKeyPrefix}-${grupoPai.grupoPai}`} className={styles.groupParentItem}>
                        <div className={styles.groupParentRow}>
                            <div className={styles.groupParentLeft}>
                                <button
                                    type="button"
                                    className={styles.expandButton}
                                    onClick={() => handleToggle(grupoPai.grupoPai)}
                                    aria-expanded={isExpanded}
                                >
                                    {isExpanded ? '−' : '+'}
                                </button>

                                <strong className={styles.groupParentName}>{grupoPai.grupoPai}</strong>
                            </div>

                            <div className={styles.groupNumbers}>
                                <span>D: {formatNumber(grupoPai.qtdDistribuida)} pç</span>
                                <span>{formatCurrency(grupoPai.valorDistribuido)}</span>
                                <span>V: {formatNumber(grupoPai.qtdVendida)} pç</span>
                                <span>{formatCurrency(grupoPai.valorVendido)}</span>
                            </div>
                        </div>

                        {isExpanded && (
                            <ul className={styles.groupChildList}>
                                {filhos.map((filho) => (
                                    <li
                                        key={`${storageKeyPrefix}-${grupoPai.grupoPai}-${filho.grupoFilho}`}
                                        className={styles.groupChildItem}
                                    >
                                        <span className={styles.groupChildName}>{filho.grupoFilho}</span>

                                        <div className={styles.groupNumbers}>
                                            <span>D: {formatNumber(filho.qtdDistribuida)} pç</span>
                                            <span>{formatCurrency(filho.valorDistribuido)}</span>
                                            <span>V: {formatNumber(filho.qtdVendida)} pç</span>
                                            <span>{formatCurrency(filho.valorVendido)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function DistribuicaoVendas() {
    const [filtros, setFiltros] = useState(initialFiltros);
    const [options, setOptions] = useState(initialOptions);
    const [loadingData, setLoadingData] = useState(false);
    const [error, setError] = useState('');
    const [report, setReport] = useState(null);

    const filtrosPayload = useMemo(() => ({
        usuarioId: Number(localStorage.getItem('usuarioId')) || null,
        setores: filtros.setores,
        regioes: filtros.regioes,
        celulas: filtros.celulas,
        grupoPai: filtros.grupoPai
    }), [filtros.setores, filtros.regioes, filtros.celulas, filtros.grupoPai]);

    const fetchFilterOptions = async (payload = filtrosPayload) => {
        try {
            const { data } = await axios.post(`${API_URL}/distribuicoes-vendas/filtros`, payload);

            const newOptions = {
                setores: normalizeArray(data?.setores),
                regioes: normalizeArray(data?.regioes),
                celulas: normalizeArray(data?.celulas),
                gruposPai: normalizeArray(data?.gruposPai),
                gruposFilho: normalizeArray(data?.gruposFilho),
                fabricantes: normalizeArray(data?.fabricantes),
                fornecedores: normalizeArray(data?.fornecedores)
            };

            setOptions(newOptions);

            setFiltros((prev) => {
                const next = { ...prev };

                next.regioes = normalizeArray(prev.regioes).filter((item) => newOptions.regioes.includes(item));
                next.celulas = normalizeArray(prev.celulas).filter((item) => newOptions.celulas.includes(item));

                if (
                    prev.grupoFilho &&
                    !newOptions.gruposFilho.some((item) => String(item.codigo) === String(prev.grupoFilho))
                ) {
                    next.grupoFilho = '';
                }

                if (prev.grupoPai && !newOptions.gruposPai.includes(prev.grupoPai)) {
                    next.grupoPai = '';
                    next.grupoFilho = '';
                }

                if (prev.fabricante && !newOptions.fabricantes.includes(prev.fabricante)) {
                    next.fabricante = '';
                }

                if (prev.fornecedor && !newOptions.fornecedores.includes(prev.fornecedor)) {
                    next.fornecedor = '';
                }

                const unchanged =
                    JSON.stringify(prev.regioes) === JSON.stringify(next.regioes) &&
                    JSON.stringify(prev.celulas) === JSON.stringify(next.celulas) &&
                    prev.grupoPai === next.grupoPai &&
                    prev.grupoFilho === next.grupoFilho &&
                    prev.fabricante === next.fabricante &&
                    prev.fornecedor === next.fornecedor;

                return unchanged ? prev : next;
            });
        } catch (err) {
            console.error(err);
            setError('Erro ao carregar os filtros.');
        }
    };

    const fetchReport = async () => {
        try {
            setLoadingData(true);
            setError('');

            const payload = {
                usuarioId: Number(localStorage.getItem('usuarioId')) || null,
                setores: filtros.setores,
                regioes: filtros.regioes,
                celulas: filtros.celulas,
                periodoIni: filtros.periodoIni || null,
                periodoFim: filtros.periodoFim || null,
                tabelaPreco: filtros.tabelaPreco || null,
                produtos: filtros.produtos || null,
                descricao: filtros.descricao || null,
                grupoPai: filtros.grupoPai || null,
                grupoFilho: filtros.grupoFilho || null,
                fabricante: filtros.fabricante || null,
                fornecedor: filtros.fornecedor || null,
                contabilizarAlmoxarifado: filtros.contabilizarAlmoxarifado || 'NAO'
            };

            const { data } = await axios.post(`${API_URL}/distribuicoes-vendas`, payload);
            setReport(data);
        } catch (err) {
            console.error(err);
            setError('Erro ao carregar o relatório.');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        fetchFilterOptions(filtrosPayload);
    }, [filtrosPayload]);

    const handleArrayFilterChange = (field, values) => {
        setFiltros((prev) => ({
            ...prev,
            [field]: values
        }));
    };

    const handleFieldChange = (field, value) => {
        setFiltros((prev) => {
            const next = {
                ...prev,
                [field]: value
            };

            if (field === 'grupoPai') {
                next.grupoFilho = '';
            }

            return next;
        });
    };

    const handleClearFilters = () => {
        setFiltros(initialFiltros);
        setReport(null);
        setError('');
    };

    return (
        <div className={styles.body}>
            <Header />
            <Link redirectTo="/home" />

            <div className={styles.container}>
                <PageTitle
                    src="/images/common/giro_estoque.png"
                    title="Giro de Estoque"
                />

                <div className={styles.filtersCard}>
                    <div className={styles.filtersHeader}>
                        <h2 className={styles.sectionTitle}>Filtros de Pesquisa</h2>
                    </div>

                    <div className={styles.filtersGrid}>
                        {options?.setores?.length > 1 && (
                            <CheckboxGroup
                                title="Setor"
                                options={options.setores}
                                selectedValues={filtros.setores}
                                onChange={(values) => handleArrayFilterChange('setores', values)}
                            />
                        )}

                        {options?.regioes?.length > 1 && (
                            <CheckboxGroup
                                title="Região"
                                options={options.regioes}
                                selectedValues={filtros.regioes}
                                onChange={(values) => handleArrayFilterChange('regioes', values)}
                            />
                        )}

                        {options?.celulas?.length > 1 && (
                            <CheckboxGroup
                                title="Célula"
                                options={options.celulas}
                                selectedValues={filtros.celulas}
                                onChange={(values) => handleArrayFilterChange('celulas', values)}
                            />
                        )}

                        <div className={styles.dateRangeGroup}>
                            <label className={styles.filterLabel}>Período</label>
                            <div className={styles.dateInputsWrapper}>
                                <input
                                    className={styles.dateInput}
                                    type="date"
                                    value={filtros.periodoIni}
                                    onChange={(e) => handleFieldChange('periodoIni', e.target.value)}
                                    title="Período Inicial"
                                />
                                <span className={styles.dateSeparator}>até</span>
                                <input
                                    className={styles.dateInput}
                                    type="date"
                                    value={filtros.periodoFim}
                                    onChange={(e) => handleFieldChange('periodoFim', e.target.value)}
                                    title="Período Final"
                                />
                            </div>
                        </div>

                        <SelectField
                            title="Tabela de preço"
                            value={filtros.tabelaPreco}
                            onChange={(value) => handleFieldChange('tabelaPreco', value)}
                            options={[
                                { codigo: '10', nome: 'Atacado' },
                                { codigo: '7', nome: 'Consultoras Antigo' },
                                { codigo: '3', nome: 'S¹' },
                                { codigo: '12', nome: 'S²' }
                            ]}
                            placeholder="Padrão"
                        />

                        <SelectField
                            title="Contabilizar ALMOXARIFADO"
                            value={filtros.contabilizarAlmoxarifado}
                            onChange={(value) => handleFieldChange('contabilizarAlmoxarifado', value)}
                            options={['NAO', 'SIM']}
                            placeholder="Selecione"
                        />

                        <InputField
                            title="Produto(s)"
                            value={filtros.produtos}
                            onChange={(value) => handleFieldChange('produtos', value)}
                            placeholder="Ex: 67890, BR350"
                        />

                        <InputField
                            title="Descrição"
                            value={filtros.descricao}
                            onChange={(value) => handleFieldChange('descricao', value)}
                            placeholder="Ex: CORAÇÃO, INFINITO"
                        />

                        <SelectField
                            title="Grupo pai"
                            value={filtros.grupoPai}
                            onChange={(value) => handleFieldChange('grupoPai', value)}
                            options={options.gruposPai}
                        />

                        <SelectField
                            title="Grupo filho"
                            value={filtros.grupoFilho}
                            onChange={(value) => handleFieldChange('grupoFilho', value)}
                            options={options.gruposFilho}
                        />

                        <SelectField
                            title="Fabricante"
                            value={filtros.fabricante}
                            onChange={(value) => handleFieldChange('fabricante', value)}
                            options={options.fabricantes}
                        />

                        <SelectField
                            title="Fornecedor"
                            value={filtros.fornecedor}
                            onChange={(value) => handleFieldChange('fornecedor', value)}
                            options={options.fornecedores}
                        />
                    </div>

                    <div className={styles.actionsRow}>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={fetchReport}
                            disabled={loadingData}
                        >
                            Pesquisar
                        </button>

                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={handleClearFilters}
                            disabled={loadingData}
                        >
                            Limpar
                        </button>
                    </div>
                </div>

                {error && <div className={styles.errorBox}>{error}</div>}

                {loadingData && (
                    <Loading message="Carregando dados..." />
                )}

                {(!report && !loadingData) && (
                    <span style={{ textAlign: "center", marginTop: "20px" }}>Faça um filtro para começar! 😃</span>
                )}

                {report && !loadingData && (
                    <>
                        <h3 style={{ textAlign: "center", marginTop: "20px", fontSize: "2rem", backgroundColor: "#333", color: "#fff" }}>RESUMO</h3>

                        <div className={styles.summaryBar}>
                            <div className={styles.summaryGroupBase}>
                                <h4 className={styles.summaryGroupTitle}>Distribuição</h4>
                                <div className={styles.summaryItem}>
                                    <span>Total Distribuído</span>
                                    <span className={styles.summaryItemSubtitle}>Mercadoria enviada no período selecionado</span>
                                    <em>{formatCurrency(report?.header?.totalDistribuidoValor)}</em>
                                    <strong>{formatNumber(report?.header?.totalDistribuidoQtd)} pç</strong>
                                </div>
                            </div>

                            <span className={styles.summaryX}>VS</span>

                            <div className={styles.summaryGroupSales}>
                                <h4 className={styles.summaryGroupTitle}>Vendas Realizadas</h4>
                                <div className={styles.salesGrid}>
                                    <div className={styles.summaryItem}>
                                        <span>Da Distribuição Atual</span>
                                        <span className={styles.summaryItemSubtitle}>Referente ao total distribuído</span>
                                        <em>{formatCurrency(report?.header?.totalVendidoDistribuidoValor)}</em>
                                        <strong>{formatNumber(report?.header?.totalVendidoDistribuidoQtd)} pç</strong>
                                    </div>

                                    <span className={styles.summaryMathSign}>+</span>

                                    <div className={styles.summaryItem}>
                                        <span>De Distribuições Anteriores</span>
                                        <span className={styles.summaryItemSubtitle}>Estoque acumulado passado</span>
                                        <em>{formatCurrency((report?.header?.totalVendidoValor || 0) - (report?.header?.totalVendidoDistribuidoValor || 0))}</em>
                                        <strong>{formatNumber((report?.header?.totalVendidoQtd || 0) - (report?.header?.totalVendidoDistribuidoQtd || 0))} pç</strong>
                                    </div>

                                    <span className={styles.summaryMathSign}>=</span>

                                    <div className={`${styles.summaryItem} ${styles.summaryItemTotal}`}>
                                        <span>Total Geral</span>
                                        <span className={styles.summaryItemSubtitle}>Todas as vendas do período</span>
                                        <em>{formatCurrency(report?.header?.totalVendidoValor)}</em>
                                        <strong>{formatNumber(report?.header?.totalVendidoQtd)} pç</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.reportCompactGrid}>
                            <div className={styles.reportCard}>
                                <GrupoHierarquicoCompacto
                                    grupos={report?.header?.detalhamentoGrupos}
                                    storageKeyPrefix="resumo"
                                />
                            </div>

                            {normalizeArray(report?.body).length > 1 && (
                                <div className={styles.reportCard}>
                                    <h3 className={styles.cardTitle}>POR CÉLULA</h3>

                                    <div className={styles.cellList}>
                                        {normalizeArray(report?.body).length === 0 ? (
                                            <div className={styles.emptyMini}>Nenhum dado encontrado.</div>
                                        ) : (
                                            normalizeArray(report?.body).map((item) => (
                                                <div
                                                    key={`${item.nome}-${item.empresa}`}
                                                    className={styles.cellCompactCard}
                                                >
                                                    <div className={styles.cellTopRow}>
                                                        <div className={styles.cellName}>📌 {item.nome}</div>

                                                        <div className={styles.cellBadges}>
                                                            <span>{item.setor}</span>
                                                            <span>{item.regiao}</span>
                                                            <span>Emp. {item.empresa}</span>
                                                            <span>Cli. {item.cliente}</span>
                                                        </div>
                                                    </div>

                                                    <div className={styles.cellTotalsInline}>
                                                        <span>
                                                            <strong>Distribuído:</strong> {formatNumber(item.totalDistribuidoQtd)} pç
                                                        </span>
                                                        <span>{formatCurrency(item.totalDistribuidoValor)}</span>
                                                        <span>
                                                            <strong>Vendido:</strong> {formatNumber(item.totalVendidoQtd)} pç
                                                        </span>
                                                        <span>{formatCurrency(item.totalVendidoValor)}</span>
                                                    </div>

                                                    {(
                                                        item.totalVendidoQtd > 0 &&
                                                        item.totalDistribuidoQtd > 0 &&
                                                        item.totalVendidoValor > 0 &&
                                                        item.totalDistribuidoValor > 0)
                                                        && (
                                                            <div className={styles.percentualVendido}>
                                                                <p>
                                                                    % vendido (qtd) → <strong>{((item.totalVendidoQtd / item.totalDistribuidoQtd) * 100).toFixed(0)}%</strong>
                                                                </p>

                                                                <p>
                                                                    % vendido (valor) → <strong>{((item.totalVendidoValor / item.totalDistribuidoValor) * 100).toFixed(0)}%</strong>
                                                                </p>
                                                            </div>
                                                        )
                                                    }

                                                    <GrupoHierarquicoCompacto
                                                        grupos={item.detalhamentoGrupos}
                                                        storageKeyPrefix={`celula-${item.empresa}`}
                                                    />
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default DistribuicaoVendas;